import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Alert,
  Divider,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { GoogleLoginButton } from '../components/GoogleLoginButton';
import { useAuth } from '../hooks/useAuth';

/**
 * Google 認證測試頁面
 *
 * 用於測試 Firebase Google OAuth 整合
 */
export const TestGoogleAuthPage: React.FC = () => {
  const {
    signInWithGoogle,
    isFirebaseEnabled,
    isAuthenticated,
    user,
    authType,
    logout,
  } = useAuth();

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  /**
   * 處理 Google 登入
   */
  const handleGoogleLogin = async () => {
    setError(null);
    setSuccess(null);

    try {
      await signInWithGoogle();
      setSuccess('Google 登入成功！');
    } catch (err: any) {
      console.error('Google 登入失敗:', err);
      setError(err.message || 'Google 登入失敗');
    }
  };

  /**
   * 處理登出
   */
  const handleLogout = async () => {
    try {
      await logout();
      setSuccess('已登出');
      setError(null);
    } catch (err: any) {
      setError('登出失敗');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Paper elevation={6} sx={{ p: 4, borderRadius: 2 }}>
          {/* 標題 */}
          <Box textAlign="center" mb={3}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              🧪 Firebase Google 認證測試
            </Typography>
            <Typography variant="body1" color="text.secondary">
              測試 Firebase Authentication 整合
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Firebase 狀態 */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📊 系統狀態
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    Firebase 狀態:
                  </Typography>
                  <Chip
                    label={isFirebaseEnabled ? '已啟用' : '未啟用'}
                    color={isFirebaseEnabled ? 'success' : 'error'}
                    size="small"
                  />
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    認證狀態:
                  </Typography>
                  <Chip
                    label={isAuthenticated ? '已登入' : '未登入'}
                    color={isAuthenticated ? 'success' : 'default'}
                    size="small"
                  />
                </Box>

                {authType && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="text.secondary">
                      認證類型:
                    </Typography>
                    <Chip label={authType.toUpperCase()} size="small" />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* 使用者資訊 */}
          {isAuthenticated && user && (
            <Card variant="outlined" sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  👤 使用者資訊
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography variant="body2">
                    <strong>Email:</strong> {user.email}
                  </Typography>
                  <Typography variant="body2">
                    <strong>姓名:</strong> {user.full_name || '未設定'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>餘額:</strong> NTD {user.balance}
                  </Typography>
                  <Typography variant="body2">
                    <strong>使用者 ID:</strong> {user.user_id}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* 錯誤訊息 */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* 成功訊息 */}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* Firebase 未啟用警告 */}
          {!isFirebaseEnabled && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Firebase Authentication 未啟用。請檢查 .env.development 檔案中的設定。
            </Alert>
          )}

          {/* 操作按鈕 */}
          <Box display="flex" flexDirection="column" gap={2}>
            {!isAuthenticated ? (
              <>
                <GoogleLoginButton
                  onClick={handleGoogleLogin}
                  text="測試 Google 登入"
                />

                <Typography variant="caption" color="text.secondary" textAlign="center">
                  點擊按鈕將開啟 Google OAuth 彈窗
                </Typography>
              </>
            ) : (
              <>
                <Alert severity="success">
                  ✅ 已成功登入！您可以前往 Dashboard 或登出。
                </Alert>

                <GoogleLoginButton
                  onClick={handleLogout}
                  text="登出"
                  sx={{
                    borderColor: '#f44336',
                    color: '#f44336',
                    '&:hover': {
                      borderColor: '#d32f2f',
                      backgroundColor: '#ffebee',
                    },
                  }}
                />
              </>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* 說明 */}
          <Box>
            <Typography variant="h6" gutterBottom>
              📝 測試步驟
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              <ol>
                <li>確認 Firebase 狀態為「已啟用」</li>
                <li>點擊「測試 Google 登入」按鈕</li>
                <li>選擇 Google 帳號並授權</li>
                <li>檢查是否顯示使用者資訊</li>
                <li>開啟瀏覽器開發者工具查看 Console 日誌</li>
                <li>檢查 Network 標籤中的 API 請求</li>
              </ol>
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* 除錯資訊 */}
          <Box>
            <Typography variant="h6" gutterBottom>
              🔍 除錯資訊
            </Typography>
            <Typography variant="body2" color="text.secondary">
              請開啟瀏覽器開發者工具 (F12) 查看詳細日誌：
            </Typography>
            <Box
              component="pre"
              sx={{
                mt: 1,
                p: 2,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                overflow: 'auto',
                fontSize: '12px',
              }}
            >
              {JSON.stringify(
                {
                  isFirebaseEnabled,
                  isAuthenticated,
                  authType,
                  userEmail: user?.email || null,
                },
                null,
                2
              )}
            </Box>
          </Box>
        </Paper>

        {/* 頁尾 */}
        <Box textAlign="center" mt={3}>
          <Typography variant="body2" color="white">
            Firebase Authentication Integration Test Page
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default TestGoogleAuthPage;
